import React, { useCallback, useMemo, useState } from 'react';
import { Modal } from 'components/common/Modal/Modal';
import i18n from 'i18n';
import styles from './downloadReportPanel.module.scss';
import { InputField } from 'components/common/form/field/InputField';
import { RadioField } from 'components/common/form/field/RadioField';
import { validateEmpty } from 'utils/ValidateUtils';

type DownloadOutdoorFinalReportPanelProps = {
  onGenerate: (targetImpressions: number, showTop20Stores: boolean) => void;
  onClose: () => void;
};

export const DownloadOutdoorFinalReportPanel: React.FC<
  DownloadOutdoorFinalReportPanelProps
> = ({ onGenerate, onClose }) => {
  const [targetImpressions, setTargetImpressions] = useState<string>('');
  const [showTop20Stores, setShowTop20Stores] = useState<boolean>(true);
  const [submitCount, setSubmitCount] = useState<number>(0);

  const handleTargetImpressionsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTargetImpressions(e.target.value);
    },
    []
  );

  const targetImpressionsError = useMemo(() => {
    return validateEmpty(targetImpressions);
  }, [targetImpressions]);

  const handleGenerate = useCallback(() => {
    setSubmitCount((prev) => prev + 1);
    if (!targetImpressionsError) {
      onGenerate(Number(targetImpressions) || 0, showTop20Stores);
    }
  }, [targetImpressions, showTop20Stores, onGenerate, targetImpressionsError]);

  const yesNoOptions = useMemo(
    () => [
      { label: i18n.t('common.labels.yes'), value: 1 },
      { label: i18n.t('common.labels.no'), value: 0 }
    ],
    []
  );

  const onShowTop20StoresChange = useCallback((value: number | string) => {
    setShowTop20Stores(Number(value) === 1);
  }, []);

  return (
    <Modal
      title={i18n.t('downloadOutdoorFinalReportPanel.title')}
      dismiss={onClose}
      animation={false}
      className={styles.downloadReportPanel}
      fullScreen
      enforceFocus={false}
      primaryButton={{
        title: i18n.t('downloadOutdoorFinalReportPanel.buttons.generate'),
        callback: handleGenerate
      }}
      secondaryButton={{
        title: i18n.t('common.buttons.cancel'),
        callback: onClose
      }}
    >
      <div className={styles.content}>
        <InputField
          name='targetImpressions'
          label={i18n.t(
            'downloadOutdoorFinalReportPanel.labels.targetImpressions'
          )}
          type='number'
          value={targetImpressions}
          onChange={handleTargetImpressionsChange}
          error={submitCount > 0 ? targetImpressionsError : undefined}
        />
        <RadioField
          name='showTop20Stores'
          label={i18n.t(
            'downloadOutdoorFinalReportPanel.labels.showTop20Stores'
          )}
          options={yesNoOptions}
          value={showTop20Stores ? 1 : 0}
          onChange={onShowTop20StoresChange}
        />
      </div>
    </Modal>
  );
};
